import { QueryClient } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'

/** The 4xx answers that say "not now" rather than "no": timed out, too early, rate limited. */
const NOT_NOW_STATUSES: ReadonlySet<number> = new Set([408, 425, 429])

/**
 * Whether asking again may get a different answer: a 5xx, a 4xx that only
 * says "not now", or a network drop. Any other 4xx is the server's answer (a
 * cross-tenant or authorisation refusal, a lapsed session), and any other
 * exception is this app's own failure, which asking again repeats.
 *
 * `fetch` reports a network drop only as a `TypeError`, worded differently by
 * every browser, so every `TypeError` counts as one: a programming error of
 * that type costs two bounded retries, where the alternative would never
 * retry a dropped connection.
 */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500 || NOT_NOW_STATUSES.has(error.status)
  return error instanceof TypeError
}

/**
 * Only a retryable failure is asked again: twice, after 1 s and 2 s
 * (TanStack's default delay: exponential, capped at 30 s). Retrying a refusal
 * would turn one clear answer into three.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => isRetryableError(error) && failureCount < 2,
    },
  },
})
